// biome-ignore lint/style/useImportType: <tbd> IApiClientRequestOptions?
import {
  IApiClientRequestOptions,
  parseApiClientFullResponse,
} from '@framework/api-client/client/apiClient.ts';
import { t } from '@framework/i18n/intl/i18next.ts';
import { message } from 'antd';
import { type AxiosRequestHeaders } from 'axios';
import { useMemo } from 'react';
import { useTheme } from 'styled-components';
import { useDocumentsInlineViewerContext } from '../../ui/documents/DocumentsInlineViewerContext.tsx';
import { extractPdfFromP7m, isP7mFile } from '../p7m/extractP7mContent.ts';
import { useFileLocalDownload } from './useFileLocalDownload.ts';

export interface IParsedFile {
  data: any;
  filename: string;
  mimetype?: string;
  openInNewWindow?: boolean;
}

export interface IUseFileDownloadApiOptionsParams {
  /**
   * Nome del file da scaricare.
   * Se non viene specificato, il nome del file verrà recuperato dall'header
   */
  filename?: ((data: any) => string) | string | null;
  mimetype?: string;
  parseData?: (data: any) => any;
  textResponse?: boolean;
  /** È possibile fornire una funzione che presi gli header restituisca il nome del file da scaricare */
  getFilenameFromHeaders?: (headers?: AxiosRequestHeaders) => string;
  onError?: (error: any) => void;
  onSuccess?: (data: any) => void;
  /**  opzioni aggiuntive query */
  queryOptions?: Record<string, any>;
  /**  opzioni aggiuntive per la richiesta */
  queryRequestOptions?: IApiClientRequestOptions;
  /** Header aggiuntivi */
  headers?: Record<string, string>;
  /** Apre il file in una nuova finestra */
  openInNewWindow?: boolean;
  /** Flag per verificare se siamo in backoffice */
  showInlineViewer?: boolean;
}

/**
 * Prende il nome del file dall'header (content-disposition: 'inline; filename="nomefile.pdf"')
 */
function getFilenameFromHeaders(headers?: AxiosRequestHeaders) {
  const chunks: string[] = headers?.['content-disposition']?.split(';') ?? [];
  const filenameChunk = chunks.find(chunk => chunk.includes('filename='));
  if (!filenameChunk) {
    return t('download-file.no-filename-from-header', 'Documento');
  }

  return filenameChunk.split('=')[1].replace(/"/g, '');
}

// Questa è una soluzione stabile, seppur temporanea, per evitare di
// scaricare più volte lo stesso file, per via degli `onSuccess`.
let downloadedAtLast = 0;

type InlineViewResult =
  | { action: 'view'; file: IParsedFile }
  | { action: 'download' }
  | { action: 'not-extractable'; reason: 'not-pdf' | 'error' };

/**
 * Prova a risolvere il file come PDF visualizzabile inline.
 * - `view`: file risolvibile come PDF, pronto per il Drawer
 * - `download`: non visualizzabile inline, procedere con download
 * - `not-extractable`: P7M non estraibile, il chiamante gestisce il feedback
 */
function resolveInlineViewableFile(
  parsedData: ArrayBuffer | undefined,
  parsedFile: IParsedFile,
): InlineViewResult {
  // PDF diretto
  if (parsedFile.mimetype === 'application/pdf') {
    return { action: 'view', file: parsedFile };
  }

  // Non è un P7M → non visualizzabile inline
  if (!isP7mFile(parsedFile.filename, parsedFile.mimetype)) {
    return { action: 'download' };
  }

  // P7M → per l'estrazione è richiesto un ArrayBuffer; se non disponibile, fallback a download
  if (!(parsedData instanceof ArrayBuffer)) {
    return { action: 'download' };
  }
  // P7M → tenta estrazione PDF
  const result = extractPdfFromP7m(parsedData, parsedFile.filename);

  if (result.status === 'pdf') {
    return {
      action: 'view',
      file: {
        ...parsedFile,
        data: result.data,
        filename: result.filename,
        mimetype: 'application/pdf',
      },
    };
  }

  return {
    action: 'not-extractable',
    reason: result.status === 'not-pdf' ? 'not-pdf' : 'error',
  };
}

function showP7mExtractionWarning(reason: 'not-pdf' | 'error') {
  message.warning(
    reason === 'not-pdf'
      ? t(
          'download-file.p7m-not-pdf',
          'Il file firmato non contiene un PDF. Utilizzare la funzionalità di download.',
        )
      : t(
          'download-file.p7m-extraction-failed',
          'Impossibile estrarre il contenuto dal file firmato. Utilizzare la funzionalità di download.',
        ),
  );
}

function handleInlineViewResult(
  viewResult: InlineViewResult,
  parsedFile: IParsedFile,
  onView: (file: IParsedFile) => void,
  onDownload: (file: IParsedFile) => void,
) {
  if (viewResult.action === 'view') {
    onView(viewResult.file);
  } else if (viewResult.action === 'not-extractable') {
    showP7mExtractionWarning(viewResult.reason);
    // Per P7M non estraibili, dopo il warning effettua comunque il download
    onDownload(parsedFile);
  } else {
    onDownload(parsedFile);
  }
}

function onErrorDefault(e: any) {
  message.error(
    t(
      'download-file.error-downloading-file',
      'Si è verificato un problema durante il download del file',
    ),
  );
}

/**
 * Ritorna le opzioni per le query che scaricano i file
 */
export function useFileDownloadApiOptions(
  props: IUseFileDownloadApiOptionsParams,
) {
  const {
    mimetype,
    parseData,
    textResponse = false,
    getFilenameFromHeaders: _getFilenameFromHeaders,
    onError,
    queryOptions,
    queryRequestOptions,
    headers,
  } = props;
  const handleDownloadFile = useFileLocalDownload();
  const { handleSetCurrentDocument } = useDocumentsInlineViewerContext();
  const theme = useTheme();
  const result = useMemo(() => {
    return {
      options: {
        request: {
          fullResponse: true,
          responseType: textResponse ? undefined : ('arraybuffer' as const),
          headers,
          ...queryRequestOptions,
        },
        query: {
          keepPreviousData: true,
          enabled: false,
          cacheTime: 0,
          staleTime: Number.POSITIVE_INFINITY,

          ...queryOptions,
        },
      },
      onSuccess: (data: any) => {
        if (Date.now() - downloadedAtLast < 500) return;
        downloadedAtLast = Date.now();

        const parsed = parseApiClientFullResponse(data);

        const parsedData = parseData ? parseData(parsed.data) : parsed.data;

        const filename =
          typeof props.filename === 'function'
            ? props.filename(data.data)
            : props.filename;

        const parsedFile: IParsedFile = {
          data: parsedData,
          filename:
            filename ??
            _getFilenameFromHeaders?.(parsed.headers) ??
            getFilenameFromHeaders(parsed.headers),
          mimetype: mimetype ?? parsed.headers?.getContentType()?.toString(),
          openInNewWindow: props.openInNewWindow,
        };

        const canShowInline = theme.enablePdfDrawer && props.showInlineViewer;
        const viewResult = canShowInline
          ? resolveInlineViewableFile(parsedData, parsedFile)
          : { action: 'download' as const };

        handleInlineViewResult(
          viewResult,
          parsedFile,
          handleSetCurrentDocument,
          handleDownloadFile,
        );

        props.onSuccess?.(data);
      },
      onError: onError ?? onErrorDefault,
    };
  }, [
    textResponse,
    headers,
    queryRequestOptions,
    queryOptions,
    onError,
    parseData,
    _getFilenameFromHeaders,
    mimetype,
    props,
    theme.enablePdfDrawer,
    handleSetCurrentDocument,
    handleDownloadFile,
  ]);

  return result;
}

export type FileDownloadAPiOptions = ReturnType<
  typeof useFileDownloadApiOptions
>['options'];

export type FileDownloadApiOptionsOnSuccess = ReturnType<
  typeof useFileDownloadApiOptions
>['onSuccess'];

export type FileDownloadApiOptionsOnError = ReturnType<
  typeof useFileDownloadApiOptions
>['onError'];
